import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, ChevronLeft, ChevronRight, Loader2, Home, Building2, Building, Warehouse, Briefcase, HelpCircle, BedSingle, BedDouble } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { Calendar } from "./ui/calendar";
import { trackLead } from "@/utils/gtag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_LABELS = ["Location", "Property", "Size", "Date", "Contact"];

const PROP_TYPES = [
  { id: "House", icon: Home },
  { id: "Apartment", icon: Building2 },
  { id: "Unit", icon: Building },
  { id: "Townhouse", icon: Warehouse },
  { id: "Office", icon: Briefcase },
  { id: "Other", icon: HelpCircle },
];

const SIZES = [
  { id: "Studio", icon: BedSingle },
  { id: "1 Bedroom", icon: BedSingle },
  { id: "2 Bedrooms", icon: BedDouble },
  { id: "3 Bedrooms", icon: BedDouble },
  { id: "4 Bedrooms", icon: Home },
  { id: "5+ Bedrooms", icon: Home },
];

const SIZE_TO_QUOTE = {
  "Studio": "Studio / 1 Bedroom", "1 Bedroom": "Studio / 1 Bedroom", "2 Bedrooms": "2 Bedroom",
  "3 Bedrooms": "3 Bedroom", "4 Bedrooms": "4+ Bedroom", "5+ Bedrooms": "4+ Bedroom",
};
const SIZE_TO_BEDROOMS = { "Studio": "1", "1 Bedroom": "1", "2 Bedrooms": "2", "3 Bedrooms": "3", "4 Bedrooms": "4", "5+ Bedrooms": "4+" };

const inputCls =
  "w-full rounded-2xl border border-line bg-white px-4 py-3 text-base text-slate-900 placeholder-zinc-400 outline-none transition-colors focus:border-blaze sm:text-sm";

const StepTitle = ({ title, sub }) => (
  <div className="mb-4">
    <h3 className="font-display text-lg font-semibold tracking-tight text-slate-900">{title}</h3>
    {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
  </div>
);

const Card = ({ icon: Icon, label, active, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-3 rounded-2xl border-2 p-4 text-left transition-all duration-200 active:scale-[0.98] ${
      active ? "border-blaze bg-blaze/[0.06] shadow-[0_8px_24px_rgba(37,99,235,0.12)]" : "border-line bg-white hover:border-blaze/50"
    }`}
  >
    <Icon className={`h-5 w-5 shrink-0 ${active ? "text-blaze" : "text-slate-400"}`} strokeWidth={1.8} />
    <span className={`text-sm font-bold ${active ? "text-blaze" : "text-slate-800"}`}>{label}</span>
  </button>
);

export const QuoteForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pre = searchParams.get("truck");
  const [step, setStep] = useState(1);
  const [tried, setTried] = useState(false);
  const [state, setState] = useState("idle");
  const [form, setForm] = useState({
    pickup_suburb: searchParams.get("pickup_suburb") || "",
    drop_suburb: searchParams.get("drop_suburb") || "",
    property_type: "",
    size: "",
    moving_date: searchParams.get("moving_date") || "",
    name: "", phone: searchParams.get("mobile") || "", email: searchParams.get("email") || "",
    truck: ["small", "medium", "large"].includes(pre) ? pre : "",
  });
  const patch = (p) => setForm((f) => ({ ...f, ...p }));

  useEffect(() => {
    const p = searchParams.get("pickup_suburb");
    const d = searchParams.get("drop_suburb");
    if (p || d) patch({ pickup_suburb: p || form.pickup_suburb, drop_suburb: d || form.drop_suburb });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const valid =
    step === 1 ? form.pickup_suburb.trim() && form.drop_suburb.trim()
    : step === 2 ? !!form.property_type
    : step === 3 ? !!form.size
    : step === 4 ? !!form.moving_date
    : form.name.trim() && form.phone.trim().length >= 6 && /.+@.+\..+/.test(form.email);

  const next = () => {
    if (!valid) return setTried(true);
    setTried(false);
    setStep((s) => Math.min(5, s + 1));
  };
  const back = () => { setTried(false); setStep((s) => Math.max(1, s - 1)); };

  const submit = async () => {
    if (!valid) return setTried(true);
    setState("loading");
    try {
      const { data: q } = await axios.post(`${API}/quotes`, {
        pickup_suburb: form.pickup_suburb,
        drop_suburb: form.drop_suburb,
        moving_date: form.moving_date,
        property_size: SIZE_TO_QUOTE[form.size] || form.size,
        phone: form.phone,
        email: form.email,
        truck: form.truck || null,
        time_window: null,
        customer_name: form.name.trim(),
        property_type: form.property_type,
        bedrooms: SIZE_TO_BEDROOMS[form.size] || "",
      });
      void q;
      trackLead("quote_submitted");
      const params = new URLSearchParams({
        pickup_suburb: form.pickup_suburb,
        drop_suburb: form.drop_suburb,
        moving_date: form.moving_date,
        email: form.email,
        mobile: form.phone,
      });
      const bed = SIZE_TO_BEDROOMS[form.size];
      if (bed) params.set("bedrooms", bed);
      const thanksBase = location.pathname.startsWith("/removalists-melbourne-near-me")
        ? "/removalists-melbourne-near-me/thank-you"
        : "/thank-you";
      navigate(`${thanksBase}?type=quote&${params.toString()}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setState("idle");
    }
  };

  return (
    <section id="quote" className="relative border-y border-line bg-white/75" data-testid="quote-section">
      <div className="mx-auto grid max-w-7xl gap-8 px-5 py-12 sm:py-16 lg:grid-cols-12 lg:gap-12 lg:px-10 lg:py-32">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-blaze">Melbourne Removalists From $69/hr</p>
            <h2 className="font-display text-2xl font-semibold uppercase leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl">
              Get your free<br />moving <span className="text-blaze">quote</span>
            </h2>
            <p className="mt-4 max-w-md text-xs leading-relaxed text-slate-600 md:text-base">
              Thirty seconds of your time. No obligations, no hidden fees — just an
              honest price from Melbourne&rsquo;s most reliable moving crew.
            </p>
            <div className="mt-6 flex items-center gap-3 border border-line bg-slate-100/70 p-3.5 lg:mt-10 lg:p-5">
              <Phone className="h-6 w-6 shrink-0 text-blaze lg:h-8 lg:w-8" strokeWidth={1.8} />
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-500">Prefer to talk?</p>
                <a href="tel:0391136005" className="font-display text-base font-semibold text-slate-900 transition-colors hover:text-blaze lg:text-xl" data-testid="quote-call-link">
                  03 9113 6005
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-line bg-slate-50/60 p-4 sm:p-8" data-testid="quote-form">
              {(
                <>
                  <div className="mb-5" data-testid="quote-progress">
                    <div className="hidden items-center gap-1 sm:flex">
                      {STEP_LABELS.map((l, i) => (
                        <div key={l} className="flex items-center gap-1">
                          <span className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${i + 1 === step ? "text-blaze" : i + 1 < step ? "text-slate-700" : "text-slate-400"}`}>
                            <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[9px] ${i + 1 <= step ? "bg-blaze text-white" : "border border-line text-slate-400"}`}>{i + 1}</span>
                            {l}
                          </span>
                          {i < STEP_LABELS.length - 1 && <ChevronRight className="h-3 w-3 text-slate-300" />}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between sm:hidden">
                      <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">Step {step} of 5 — {STEP_LABELS[step - 1]}</span>
                    </div>
                    <div className="mt-2 h-1 overflow-hidden rounded-full bg-line/60">
                      <motion.div className="h-1 rounded-full bg-blaze" animate={{ width: `${(step / 5) * 100}%` }} transition={{ duration: 0.35 }} />
                    </div>
                  </div>

                  <div className="relative min-h-[260px] overflow-hidden">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={step}
                        initial={{ opacity: 0, x: 60 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -60 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                      >
                        {step === 1 && (
                          <div data-testid="qf-step1">
                            <StepTitle title="Where are you moving from?" />
                            <div className="space-y-3">
                              <input className={inputCls} value={form.pickup_suburb} onChange={(e) => patch({ pickup_suburb: e.target.value })} placeholder="From Address — suburb or street" data-testid="qf-from" autoComplete="off" />
                              <input className={inputCls} value={form.drop_suburb} onChange={(e) => patch({ drop_suburb: e.target.value })} placeholder="To Address — suburb or street" data-testid="qf-to" autoComplete="off" />
                            </div>
                          </div>
                        )}
                        {step === 2 && (
                          <div data-testid="qf-step2">
                            <StepTitle title="What type of property are you moving?" />
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                              {PROP_TYPES.map((t) => (
                                <Card key={t.id} icon={t.icon} label={t.id} active={form.property_type === t.id} onClick={() => patch({ property_type: t.id })} testid={`qf-type-${t.id.toLowerCase()}`} />
                              ))}
                            </div>
                          </div>
                        )}
                        {step === 3 && (
                          <div data-testid="qf-step3">
                            <StepTitle title="How many bedrooms?" />
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                              {SIZES.map((s) => (
                                <Card key={s.id} icon={s.icon} label={s.id} active={form.size === s.id} onClick={() => patch({ size: s.id })} testid={`qf-size-${s.id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
                              ))}
                            </div>
                          </div>
                        )}
                        {step === 4 && (
                          <div data-testid="qf-step4">
                            <StepTitle title="When are you moving?" sub="Pick your preferred moving date." />
                            <div className="inline-block rounded-2xl border border-line bg-white p-2" data-testid="qf-calendar">
                              <Calendar
                                mode="single"
                                selected={form.moving_date ? new Date(form.moving_date + "T00:00:00") : undefined}
                                onSelect={(d) => d && patch({ moving_date: d.toLocaleDateString("en-CA") })}
                                disabled={{ before: new Date() }}
                              />
                            </div>
                            {form.moving_date && (
                              <p className="mt-3 text-sm font-semibold text-blaze" data-testid="qf-date-selected">
                                Moving date: {new Date(form.moving_date + "T00:00:00").toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                              </p>
                            )}
                          </div>
                        )}
                        {step === 5 && (
                          <div data-testid="qf-step5">
                            <StepTitle title="Your contact details" sub="We'll call you back within 10–15 minutes with your price." />
                            <div className="space-y-4">
                              <div>
                                <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Full Name *</label>
                                <input className={inputCls} value={form.name} onChange={(e) => patch({ name: e.target.value })} placeholder="e.g. John Smith" data-testid="qf-name" />
                              </div>
                              <div>
                                <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Phone Number *</label>
                                <input type="tel" className={inputCls} value={form.phone} onChange={(e) => patch({ phone: e.target.value })} placeholder="04XX XXX XXX" data-testid="qf-phone" />
                              </div>
                              <div>
                                <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Email Address *</label>
                                <input type="email" className={inputCls} value={form.email} onChange={(e) => patch({ email: e.target.value })} placeholder="you@email.com" data-testid="qf-email" />
                              </div>
                            </div>
                          </div>
                        )}

                        {tried && !valid && (
                          <p className="mt-4 text-sm font-semibold text-blaze" data-testid={`qf-step${step}-error`}>
                            {step === 5 ? "Please fill in your name, phone and a valid email." : "Please complete this step to continue."}
                          </p>
                        )}

                        <div className="mt-6 flex flex-col gap-2">
                          {step < 5 ? (
                            <button type="button" onClick={next} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-blaze py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-all duration-200 hover:bg-blue-700 active:scale-[0.98]" data-testid="qf-continue">
                              Continue <ChevronRight className="h-4 w-4" />
                            </button>
                          ) : (
                            <button type="button" onClick={submit} disabled={state === "loading"} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-blaze py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-all duration-200 hover:bg-blue-700 active:scale-[0.98] disabled:opacity-60" data-testid="quote-submit-btn">
                              {state === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : "Request My Quote"}
                            </button>
                          )}
                          {step > 1 && (
                            <button type="button" onClick={back} className="flex w-full items-center justify-center gap-1.5 py-2 text-xs font-bold uppercase tracking-widest text-slate-400 transition-colors hover:text-slate-700" data-testid="qf-back">
                              <ChevronLeft className="h-4 w-4" /> Back
                            </button>
                          )}
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
